import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Section, Card, Button, Alert, Input, Label, Badge } from "../design-system";
import { useAuth } from "../auth/AuthProvider";

export function AdminPriceListsPage() {
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [lists, setLists] = useState(null);
  const [cats, setCats] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  // Feb 2026 v34 — Create modal state
  const [showCreate, setShowCreate] = useState(false);
  const [nCode, setNCode] = useState("");
  const [nName, setNName] = useState("");
  const [nFrom, setNFrom] = useState(new Date().toISOString().slice(0, 10));
  const [nTo, setNTo] = useState("");
  const [nTarget, setNTarget] = useState("DEFAULT"); // DEFAULT | CATEGORY
  const [nCatId, setNCatId] = useState("");
  // Feb 2026 v34 — Customer→list assignment mini-panel
  const [showAssign, setShowAssign] = useState(false);
  const [assignCustQuery, setAssignCustQuery] = useState("");
  const [assignCustId, setAssignCustId] = useState("");
  const [assignListId, setAssignListId] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignMsg, setAssignMsg] = useState(null);

  const load = async () => {
    try {
      const [pls, cs, custs] = await Promise.all([
        apiGet("/api/admin/price-lists"),
        apiGet("/api/customer-categories").catch(() => []),
        apiGet("/api/customers").catch(() => []),
      ]);
      setLists(pls);
      setCats(Array.isArray(cs) ? cs : []);
      setCustomers(Array.isArray(custs) ? custs : []);
    }
    catch (e) { setErr(String(e?.message || e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const catNameById = useMemo(() => {
    const m = {};
    for (const c of cats) m[c.id] = c.name || c.code;
    return m;
  }, [cats]);

  const audienceLabel = (pl) => {
    if (pl.is_default) return "Tutti i clienti (DEFAULT)";
    if (pl.customer_category_id) return `Categoria: ${catNameById[pl.customer_category_id] || "—"}`;
    return "—";
  };

  const usedByLabel = (pl) => {
    // Feb 2026 v34 — Direct override + inherited via category
    const d = pl.customers_count_direct || 0;
    const i = pl.customers_count_inherited || 0;
    const parts = [];
    if (d > 0) parts.push(`${d} diretto${d === 1 ? "" : "i"}`);
    if (i > 0) parts.push(`${i} da categoria`);
    if (parts.length === 0) return "—";
    return parts.join(" · ");
  };

  const submitCreate = async () => {
    setErr(null);
    if (!nCode.trim() || !nName.trim() || !nFrom) {
      setErr("Codice, Nome e Data inizio sono obbligatori"); return;
    }
    if (nTarget === "CATEGORY" && !nCatId) {
      setErr("Seleziona una categoria clienti"); return;
    }
    setBusy(true);
    try {
      const payload = {
        code: nCode.trim(), name: nName.trim(),
        valid_from: nFrom, valid_to: nTo || null,
        is_active: true,
        is_default: nTarget === "DEFAULT",
        customer_category_id: nTarget === "CATEGORY" ? nCatId : null,
      };
      await apiPost("/api/admin/price-lists", payload);
      setShowCreate(false);
      setNCode(""); setNName(""); setNTo(""); setNTarget("DEFAULT"); setNCatId("");
      await load();
    } catch (e) {
      const d = e?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.code || String(e?.message || e)));
    } finally { setBusy(false); }
  };

  const toggleDefault = async (pl) => {
    setBusy(true); setErr(null);
    try {
      await apiPatch(`/api/admin/price-lists/${pl.id}`, { is_default: !pl.is_default });
      await load();
    } catch (e) { setErr(e?.data?.detail?.code || String(e?.message || e)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (pl) => {
    setBusy(true); setErr(null);
    try {
      await apiPatch(`/api/admin/price-lists/${pl.id}`, { is_active: !pl.is_active });
      await load();
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setBusy(false); }
  };

  const filteredCust = useMemo(() => {
    const q = assignCustQuery.trim().toLowerCase();
    if (!q) return [];
    return customers.filter(c =>
      (c.code || "").toLowerCase().includes(q) ||
      (c.business_name || "").toLowerCase().includes(q)
    ).slice(0, 8);
  }, [assignCustQuery, customers]);

  const submitAssign = async () => {
    setAssignMsg(null);
    if (!assignCustId) { setAssignMsg({ kind: "error", text: "Seleziona un cliente" }); return; }
    setAssignBusy(true);
    try {
      await apiPatch(`/api/admin/customers/${assignCustId}/price-list`,
        { price_list_id: assignListId || null });
      const cust = customers.find(c => c.id === assignCustId);
      const listName = assignListId
        ? (lists.find(l => l.id === assignListId)?.code || "?")
        : "Nessun listino (torna al fallback categoria/DEFAULT)";
      setAssignMsg({ kind: "ok",
        text: `${cust?.business_name || cust?.code || "Cliente"} → ${listName}` });
      setAssignCustQuery(""); setAssignCustId(""); setAssignListId("");
      await load();
    } catch (e) {
      setAssignMsg({ kind: "error", text: String(e?.message || e) });
    } finally { setAssignBusy(false); }
  };

  if (!lists) return <Section title="Listini prezzi"><Card><Card.Body className="muted">Caricamento…</Card.Body></Card></Section>;

  return (
    <Section title="Listini prezzi" data-testid="admin-price-lists-page">
      {err && <Alert tone="danger" data-testid="pl-list-error">{err}</Alert>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
        <p className="muted" style={{ margin: 0, fontSize: ".85rem", flex: 1, minWidth: 260 }}>
          Gerarchia prezzi: <strong>Override cliente</strong> → <strong>Listino categoria</strong> → <strong>DEFAULT</strong>.
        </p>
        <Button onClick={() => setShowAssign(v => !v)}
                data-testid="pl-assign-toggle">
          {showAssign ? "Chiudi assegnazione" : "Assegna listino a cliente"}
        </Button>
        <Button variant="primary" onClick={() => setShowCreate(true)}
                data-testid="pl-new-btn">+ Nuovo listino</Button>
      </div>
      {showAssign && (
        <Card style={{ marginBottom: 12 }} data-testid="pl-assign-panel">
          <Card.Header><strong>Assegna listino a un cliente specifico</strong></Card.Header>
          <Card.Body>
            {assignMsg && (
              <Alert tone={assignMsg.kind === "ok" ? "success" : "danger"}
                     data-testid="pl-assign-msg">{assignMsg.text}</Alert>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr auto", gap: 8, alignItems: "end" }}>
              <div>
                <Label>Cerca cliente (codice o ragione sociale)</Label>
                <Input value={assignCustQuery} data-testid="pl-assign-cust-query"
                       onChange={e => { setAssignCustQuery(e.target.value); setAssignCustId(""); }}
                       placeholder="Es. 0002030 o SPEEDYFOOD" />
                {filteredCust.length > 0 && !assignCustId && (
                  <div style={{ border: "1px solid #e2e8f0", marginTop: 4, borderRadius: 6, maxHeight: 180, overflow: "auto" }}>
                    {filteredCust.map(c => (
                      <div key={c.id}
                           data-testid={`pl-assign-cust-opt-${c.code}`}
                           onClick={() => { setAssignCustId(c.id); setAssignCustQuery(`${c.code} — ${c.business_name}`); }}
                           style={{ padding: "6px 8px", cursor: "pointer", fontSize: ".82rem", borderBottom: "1px solid #f1f5f9" }}>
                        <strong>{c.code}</strong> — {c.business_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label>Listino da assegnare</Label>
                <select value={assignListId} onChange={e => setAssignListId(e.target.value)}
                        data-testid="pl-assign-list-select"
                        style={{ width: "100%", padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
                  <option value="">Nessun listino (fallback categoria/DEFAULT)</option>
                  {lists.filter(l => l.is_active && !l.is_default).map(l => (
                    <option key={l.id} value={l.id}>{l.code} — {l.name}</option>
                  ))}
                </select>
              </div>
              <Button variant="primary" onClick={submitAssign} disabled={assignBusy || !assignCustId}
                      data-testid="pl-assign-submit">Assegna</Button>
            </div>
            <div className="muted" style={{ fontSize: ".76rem", marginTop: 6 }}>
              Se lasci "Nessun listino", il cliente eredita dalla categoria (se assegnata) o dal DEFAULT.
              La gerarchia pricing esistente resta invariata.
            </div>
          </Card.Body>
        </Card>
      )}
      {showCreate && (
        <Card style={{ marginBottom: 12 }} data-testid="pl-create-form">
          <Card.Header><strong>Nuovo listino</strong></Card.Header>
          <Card.Body>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
              <div><Label>Codice *</Label><Input value={nCode} data-testid="pl-new-code"
                                                  onChange={e => setNCode(e.target.value)} /></div>
              <div><Label>Nome *</Label><Input value={nName} data-testid="pl-new-name"
                                                onChange={e => setNName(e.target.value)} /></div>
              <div><Label>Dal *</Label><Input type="date" value={nFrom} data-testid="pl-new-from"
                                              onChange={e => setNFrom(e.target.value)} /></div>
              <div><Label>Al (opz.)</Label><Input type="date" value={nTo} data-testid="pl-new-to"
                                                  onChange={e => setNTo(e.target.value)} /></div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Label>Destinatario *</Label>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <input type="radio" name="pl-target" value="DEFAULT"
                         checked={nTarget === "DEFAULT"}
                         data-testid="pl-new-target-default"
                         onChange={() => setNTarget("DEFAULT")} />
                  Tutti i clienti (DEFAULT)
                </label>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <input type="radio" name="pl-target" value="CATEGORY"
                         checked={nTarget === "CATEGORY"}
                         data-testid="pl-new-target-category"
                         onChange={() => setNTarget("CATEGORY")} />
                  Categoria clienti
                </label>
                {nTarget === "CATEGORY" && (
                  <select value={nCatId} onChange={e => setNCatId(e.target.value)}
                          data-testid="pl-new-category-select"
                          style={{ padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
                    <option value="">— seleziona categoria —</option>
                    {cats.map(c => (<option key={c.id} value={c.id}>{c.code} — {c.name}</option>))}
                  </select>
                )}
              </div>
              <div className="muted" style={{ fontSize: ".76rem", marginTop: 4 }}>
                Per assegnare a un <strong>cliente specifico</strong>: apri il profilo cliente → sezione Listino.
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              <Button onClick={() => setShowCreate(false)} data-testid="pl-new-cancel">Annulla</Button>
              <Button variant="primary" onClick={submitCreate} loading={busy} disabled={busy}
                      data-testid="pl-new-submit">Crea listino</Button>
            </div>
          </Card.Body>
        </Card>
      )}
      <Card>
        <Card.Body>
          <div className="ds-table-wrap">
            <table className="ds-table" data-testid="admin-pl-table">
              <thead>
                <tr>
                  <th>Codice</th><th>Nome</th><th>Destinatario</th><th>Usato da</th><th>Validità</th>
                  <th>Voci</th><th>Attivo</th><th>Default</th><th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {lists.map(pl => (
                  <tr key={pl.id} data-testid={`pl-row-${pl.code}`}>
                    <td><strong>{pl.code}</strong></td>
                    <td>{pl.name}</td>
                    <td data-testid={`pl-audience-${pl.code}`}>{audienceLabel(pl)}</td>
                    <td data-testid={`pl-usedby-${pl.code}`}>{usedByLabel(pl)}</td>
                    <td>{pl.valid_from} → {pl.valid_to || "∞"}</td>
                    <td data-testid={`pl-items-count-${pl.code}`}>{pl.items_count}</td>
                    <td>
                      <Badge tone={pl.is_active ? "success" : "neutral"}>
                        {pl.is_active ? "SÌ" : "NO"}
                      </Badge>
                    </td>
                    <td>
                      {pl.is_default
                        ? <Badge tone="warning" data-testid={`pl-default-badge-${pl.code}`}>DEFAULT</Badge>
                        : <span className="muted">—</span>}
                    </td>
                    <td style={{ display: "flex", gap: 6 }}>
                      <Link to={`/admin/price-lists/${pl.id}`}>
                        <Button size="sm" data-testid={`pl-open-${pl.code}`}>Apri</Button>
                      </Link>
                      <Button size="sm" onClick={() => toggleDefault(pl)} loading={busy} disabled={busy}
                              data-testid={`pl-toggle-default-${pl.code}`}>
                        {pl.is_default ? "Rimuovi DEFAULT" : "Imposta DEFAULT"}
                      </Button>
                      <Button size="sm" onClick={() => toggleActive(pl)} loading={busy} disabled={busy}
                              data-testid={`pl-toggle-active-${pl.code}`}>
                        {pl.is_active ? "Disattiva" : "Attiva"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
    </Section>
  );
}


export function AdminPriceListDetailPage() {
  const { id } = useParams();
  const { apiGet, apiPost, apiPatch, apiDelete } = useAuth();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState(null);
  // Add-item form
  const [newSku, setNewSku] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newMinQty, setNewMinQty] = useState("1");
  const [newNotes, setNewNotes] = useState("");
  // Edit-item
  const [editingId, setEditingId] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [editMinQty, setEditMinQty] = useState("");

  const load = async () => {
    try {
      const qs = q ? `?q=${encodeURIComponent(q)}` : "";
      setItems(await apiGet(`/api/admin/price-lists/${id}/items${qs}`));
    } catch (e) { setErr(String(e?.message || e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addItem = async () => {
    setErr(null);
    if (!newSku || !newPrice) { setErr("SKU e prezzo obbligatori"); return; }
    try {
      await apiPost(`/api/admin/price-lists/${id}/items`, {
        sku: newSku.trim(), unit_price: Number(newPrice),
        min_qty: Number(newMinQty) || 1, notes: newNotes || null,
      });
      setNewSku(""); setNewPrice(""); setNewMinQty("1"); setNewNotes("");
      await load();
    } catch (e) {
      const d = e?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.code || String(e?.message || e)));
    }
  };

  const saveEdit = async (item) => {
    setErr(null);
    try {
      await apiPatch(`/api/admin/price-list-items/${item.id}`, {
        unit_price: editPrice !== "" ? Number(editPrice) : undefined,
        min_qty: editMinQty !== "" ? Number(editMinQty) : undefined,
      });
      setEditingId(null);
      await load();
    } catch (e) { setErr(String(e?.message || e)); }
  };

  const del = async (item) => {
    if (!window.confirm(`Rimuovere il prezzo per ${item.sku}?`)) return;
    try { await apiDelete(`/api/admin/price-list-items/${item.id}`); await load(); }
    catch (e) { setErr(String(e?.message || e)); }
  };

  return (
    <Section title="Dettaglio Listino" data-testid="admin-price-list-detail">
      <Link to="/admin/price-lists"><Button size="sm">← Torna ai listini</Button></Link>
      {err && <Alert tone="danger" data-testid="pl-detail-error" style={{ marginTop: 8 }}>{err}</Alert>}

      <Card style={{ marginTop: 12 }}>
        <Card.Header><strong>Aggiungi riga prezzo</strong></Card.Header>
        <Card.Body>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr auto", gap: 8, alignItems: "end" }}>
            <div><Label>SKU *</Label><Input value={newSku} data-testid="pl-add-sku"
                                              onChange={e => setNewSku(e.target.value)} /></div>
            <div><Label>Prezzo * (€/KG)</Label><Input type="number" step="0.0001" value={newPrice}
                                                        data-testid="pl-add-price"
                                                        onChange={e => setNewPrice(e.target.value)} /></div>
            <div><Label>Min qty</Label><Input type="number" min="1" value={newMinQty}
                                              data-testid="pl-add-minqty"
                                              onChange={e => setNewMinQty(e.target.value)} /></div>
            <div><Label>Note</Label><Input value={newNotes} data-testid="pl-add-notes"
                                            onChange={e => setNewNotes(e.target.value)} /></div>
            <Button variant="primary" onClick={addItem} data-testid="pl-add-submit">Aggiungi</Button>
          </div>
        </Card.Body>
      </Card>

      <Card style={{ marginTop: 12 }}>
        <Card.Header>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <strong>Righe prezzo</strong>
            <Input placeholder="Cerca SKU o descrizione…" value={q}
                   data-testid="pl-search"
                   onChange={e => setQ(e.target.value)}
                   onKeyDown={e => e.key === "Enter" && load()}
                   style={{ maxWidth: 300 }} />
            <Button size="sm" onClick={load} data-testid="pl-search-btn">Cerca</Button>
          </div>
        </Card.Header>
        <Card.Body>
          {!items ? <p className="muted">Caricamento…</p> : (
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid="pl-items-table">
                <thead>
                  <tr><th>SKU</th><th>Prodotto</th><th>Prezzo</th><th>Min qty</th><th>Note</th><th></th></tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it.id} data-testid={`pl-item-${it.sku}`}>
                      <td><strong>{it.sku}</strong></td>
                      <td>{it.name}</td>
                      <td>
                        {editingId === it.id
                          ? <Input type="number" step="0.0001" value={editPrice}
                                    data-testid={`pl-edit-price-${it.sku}`}
                                    onChange={e => setEditPrice(e.target.value)} />
                          : <span data-testid={`pl-item-price-${it.sku}`}
                                   style={{ color: it.unit_price <= 0.01 ? "#dc2626" : undefined,
                                            fontWeight: it.unit_price <= 0.01 ? 700 : undefined }}>
                              € {it.unit_price.toFixed(4)}
                            </span>}
                      </td>
                      <td>
                        {editingId === it.id
                          ? <Input type="number" min="1" value={editMinQty}
                                    data-testid={`pl-edit-minqty-${it.sku}`}
                                    onChange={e => setEditMinQty(e.target.value)} />
                          : it.min_qty}
                      </td>
                      <td>{it.notes || "—"}</td>
                      <td style={{ display: "flex", gap: 4 }}>
                        {editingId === it.id ? (
                          <>
                            <Button size="sm" variant="primary" onClick={() => saveEdit(it)}
                                    data-testid={`pl-save-${it.sku}`}>Salva</Button>
                            <Button size="sm" onClick={() => setEditingId(null)}>×</Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" onClick={() => {
                              setEditingId(it.id); setEditPrice(String(it.unit_price)); setEditMinQty(String(it.min_qty));
                            }} data-testid={`pl-edit-${it.sku}`}>Modifica</Button>
                            <Button size="sm" onClick={() => del(it)}
                                    data-testid={`pl-delete-${it.sku}`}>Rimuovi</Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Section>
  );
}
